import React, { useState, useEffect, createContext, useContext } from 'react';
import {
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  updatePassword as firebaseUpdatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider
} from "firebase/auth";
import type { User as FirebaseUser } from "firebase/auth"; // Changed to type-only import
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
  onSnapshot,
  query,
  where
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth, db } from '../firebaseConfig';
import { processProjectData, removeUndefinedValues } from '../utils'; // Assuming utils.ts is in the parent directory
import type { User, UserRole, TeamName, Project } from '../types/core'; // Import core types
import { useLanguage } from './LanguageContext'; // For syncing language preference

export interface AuthContextType {
  currentUser: User | null;
  users: User[];
  login: (identifier: string, password: string) => Promise<boolean>;
  logout: () => void;
  updateUser: (userId: string, updates: Partial<User> & { password?: string }) => Promise<boolean>;
  deleteUser: (userId: string) => Promise<boolean>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
  adminSetUserPassword: (userId: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
  authLoading: boolean;
  getAllUsers: () => User[];
  getUsersByTeam: (team: TeamName) => User[];
  getSupervisorsByTeams: (teams: TeamName[]) => User[];
  isUserAuthorizedForProject: (user: User | null, project: Project | null) => boolean;
  registerUser: (email: string, password: string, username: string, fullName: string, role: UserRole, team: TeamName) => Promise<{ success: boolean; error?: string }>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [firebaseAuthUser, setFirebaseAuthUser] = useState<FirebaseUser | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  
  // Get language context for syncing user language preference
  const languageContext = useLanguage();

  // Real-time listener for users (only for admins)
  useEffect(() => {
    if (!currentUser || currentUser.role !== 'admin') {
      setAllUsers([]);
      return;
    }

    const usersCollectionRef = collection(db, 'users');
    
    const unsubscribe = onSnapshot(usersCollectionRef, (querySnapshot) => {
      const users: User[] = [];
      querySnapshot.forEach((doc) => {
        const rawUserData = doc.data();
        const userData = processProjectData(rawUserData);
        users.push({
          id: doc.id,
          uid: doc.id,
          email: userData.email || '',
          username: userData.username || '',
          fullName: userData.fullName || '',
          role: userData.role || 'user',
          team: userData.team || 'Marketing A',
          createdAt: userData.createdAt || '',
          updatedAt: userData.updatedAt || new Date(),
          lastLogin: userData.lastLogin,
          passwordHash: ''
        } as User);
      });
      setAllUsers(users);
    }, () => {
      setAllUsers([]);
    });

    return () => {
      unsubscribe();
    };
  }, [currentUser?.id, currentUser?.role]);

  useEffect(() => {
    // Add timeout fallback for mobile devices
    const authTimeout = setTimeout(() => {
      setAuthLoading(false);
    }, 8000); // 8 second timeout for auth

    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      clearTimeout(authTimeout); // Clear timeout when auth state changes
      setFirebaseAuthUser(fbUser);
      
      if (fbUser) {
        const userProfileRef = doc(db, "users", fbUser.uid);
        try {
          const userProfileSnap = await getDoc(userProfileRef);
          if (userProfileSnap.exists()) {
            const rawUserData = userProfileSnap.data();
            const userProfileData = processProjectData(rawUserData);
            const appUser: User = {
              id: fbUser.uid,
              uid: fbUser.uid,
              email: userProfileData.email || fbUser.email || '',
              username: userProfileData.username,
              fullName: userProfileData.fullName,
              role: userProfileData.role,
              team: userProfileData.team,
              createdAt: userProfileData.createdAt || '',
              updatedAt: new Date(),
              lastLogin: fbUser.metadata.lastSignInTime || '',
              passwordHash: ''
            };
            setCurrentUser(appUser);
            
            // Sync language preference with LanguageContext
            if (languageContext) {
              languageContext.setCurrentUserId(fbUser.uid);
              if (userProfileData.preferredLanguage) {
                languageContext.setUserPreferredLanguage(userProfileData.preferredLanguage as 'th' | 'en');
              }
            }
            
            // Real-time listener will automatically load users for admins
          } else {
            await firebaseSignOut(auth);
            setCurrentUser(null);
          }
        } catch (error) {
          // Don't sign out on network errors, just set loading to false
          setCurrentUser(null);
        }
      } else {
        setCurrentUser(null);
        setAllUsers([]);
        
        // Clear user ID from language context
        if (languageContext) {
          languageContext.setCurrentUserId(null);
        }
      }
      setAuthLoading(false);
    });
    
    return () => {
      clearTimeout(authTimeout);
      unsubscribe();
    };
  }, []);

  const login = async (identifier: string, password: string): Promise<boolean> => {
    setAuthLoading(true);
    
    // Check rate limiting first
    try {
      const functionsInstance = getFunctions();
      const trackLogin = httpsCallable(functionsInstance, 'trackLoginAttempt');
      const rateLimitCheck: any = await trackLogin({ identifier, success: false });
      
      if (rateLimitCheck.data?.blocked) {
        setAuthLoading(false);
        // Return false but the caller should check for rate limit message
        return false;
      }
    } catch (rateLimitError) {
      // If rate limit check fails, continue with login attempt
    }
    
    let emailToAttemptLogin: string | null = null;

    if (identifier.includes('@')) {
      emailToAttemptLogin = identifier;
    } else {
      const lowercasedIdentifier = identifier.toLowerCase().trim();
      // First check cache with case-insensitive comparison
      let userFromCache = allUsers.find(u => u.username.toLowerCase() === lowercasedIdentifier);
      if (userFromCache && userFromCache.email && userFromCache.email.includes('@')) {
        emailToAttemptLogin = userFromCache.email;
      } else {
        // Not in cache: resolve username -> email server-side. Firestore rules
        // no longer allow reading the users collection before login.
        try {
          const functionsInstance = getFunctions();
          const resolveLoginEmail = httpsCallable(functionsInstance, 'resolveLoginEmail');
          const result = await resolveLoginEmail({ username: lowercasedIdentifier });
          const resolvedEmail = (result.data as { email?: string | null })?.email;
          if (typeof resolvedEmail === 'string' && resolvedEmail.includes('@')) {
            emailToAttemptLogin = resolvedEmail;
          }
        } catch (e) {
        }
      }
    }

    if (typeof emailToAttemptLogin === 'string' && emailToAttemptLogin.includes('@')) {
      try {
        await signInWithEmailAndPassword(auth, emailToAttemptLogin, password);
        
        // Clear rate limit on successful login
        try {
          const functionsInstance = getFunctions();
          const trackLogin = httpsCallable(functionsInstance, 'trackLoginAttempt');
          await trackLogin({ identifier, success: true });
        } catch (e) {
          // Non-critical, continue
        }
        
        // onAuthStateChanged will handle the rest
        return true;
      } catch (error: any) {
        setCurrentUser(null);
        setFirebaseAuthUser(null);
        setAuthLoading(false);
        return false;
      }
    } else {
      setAuthLoading(false);
      return false;
    }
  };

  const logout = async (): Promise<void> => {
    setAuthLoading(true);
    try {
      await firebaseSignOut(auth);
      // onAuthStateChanged will set currentUser to null and authLoading to false
    } catch (error) {
      setAuthLoading(false); // Ensure loading is false on error
    }
  };

  const registerUser = async (email: string, password: string, username: string, fullName: string, role: UserRole, team: TeamName): Promise<{ success: boolean; error?: string }> => {
    setAuthLoading(true);
    try {
      const usersRef = collection(db, "users");
      
      // Store current user to restore after registration if admin is creating user
      const currentAuthUser = auth.currentUser;
      const isAdminCreatingUser = currentUser?.role === 'admin';

      // Check username uniqueness BEFORE creating account ONLY if we are already authenticated (Admin case)
      if (currentAuthUser) {
        const qUsername = query(usersRef, where("username", "==", username));
        const usernameSnapshot = await getDocs(qUsername);
        if (!usernameSnapshot.empty) {
          setAuthLoading(false);
          return { success: false, error: 'Username already exists.' };
        }
      }

      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const fbUser = userCredential.user;

      // If we weren't authenticated before (New User case), check username NOW that we are authenticated
      if (!currentAuthUser) {
         const qUsername = query(usersRef, where("username", "==", username));
         const usernameSnapshot = await getDocs(qUsername);
         if (!usernameSnapshot.empty) {
           // Rollback: Delete the auth user we just created
           await fbUser.delete();
           setAuthLoading(false);
           return { success: false, error: 'Username already exists.' };
         }
      }

      const userProfileRef = doc(db, "users", fbUser.uid);
      const userProfileData = {
        email, 
        username, 
        fullName, 
        role, 
        team,
        uid: fbUser.uid,
        createdAt: new Date().toISOString(),
        updatedAt: new Date(),
        lastLogin: new Date().toISOString()
      };
      await setDoc(userProfileRef, userProfileData);
      
      // If admin is creating a user, sign them back in to their admin account
      if (isAdminCreatingUser && currentAuthUser) {
        await firebaseSignOut(auth);
        // The admin will need to sign back in manually for security reasons
      }
      
      // Real-time listener will automatically refresh user list
      setAuthLoading(false); // Properly reset loading state
      
      return { success: true };
    } catch (error: any) {
      setAuthLoading(false);
      let friendlyError = error.message;
      // Add more specific error messages based on Firebase error codes
      if (error.code === 'auth/email-already-in-use') {
        friendlyError = 'This email address is already registered.';
      } else if (error.code === 'auth/weak-password') {
        friendlyError = 'Password should be at least 6 characters.';
      } else if (error.code === 'auth/invalid-email') {
        friendlyError = 'Invalid email address format.';
      } else if (error.code === 'auth/network-request-failed') {
        friendlyError = 'Network error. Please check your connection and try again.';
      } else if (error.code === 'permission-denied') {
        friendlyError = 'Permission denied. Please try again.';
      }
      return { success: false, error: friendlyError };
    }
  };
  
  const changePassword = async (currentPassword: string, newPassword: string): Promise<{ success: boolean; error?: string }> => {
    if (!firebaseAuthUser || !firebaseAuthUser.email) return { success: false, error: 'User not logged in or email missing.' };
    try {
      const credential = EmailAuthProvider.credential(firebaseAuthUser.email, currentPassword);
      await reauthenticateWithCredential(firebaseAuthUser, credential);
      await firebaseUpdatePassword(firebaseAuthUser, newPassword);
      return { success: true };
    } catch (error: any) {
      // Translate error codes to user-friendly messages
      return { success: false, error: error.message };
    }
  };

  const adminSetUserPassword = async (userId: string, newPassword: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const functionsInstance = getFunctions();
      const callSetPassword = httpsCallable(functionsInstance, 'setuserpassword');
      const result: any = await callSetPassword({ uid: userId, newPassword });
      if (result?.data?.success) {
        return { success: true };
      }
      return { success: false, error: 'Unknown error while setting password.' };
    } catch (error: any) {
      let friendlyError = error?.message || 'Failed to set password.';
      if (error?.code === 'permission-denied') {
        friendlyError = 'Permission denied.';
      } else if (error?.code === 'unauthenticated') {
        friendlyError = 'Not authenticated.';
      } else if (error?.code === 'invalid-argument') {
        friendlyError = 'Invalid password or user.';
      }
      return { success: false, error: friendlyError };
    }
  };

  const updateUser = async (userId: string, updates: Partial<Omit<User, 'id' | 'passwordHash' | 'email'>>): Promise<boolean> => {
    // Password changes should be handled by changePassword or a dedicated admin function.
    // Email changes also require special handling in Firebase Auth.
    // This function should primarily update Firestore profile data.
    try {
      const userProfileRef = doc(db, "users", userId);
      await updateDoc(userProfileRef, removeUndefinedValues(updates)); // Use updateDoc for partial updates
      if (currentUser && currentUser.id === userId) {
        // If updating the current user, refresh their profile from Firestore
        const updatedProfileSnap = await getDoc(userProfileRef);
        if (updatedProfileSnap.exists()) {
          const rawUserData = updatedProfileSnap.data();
          const userProfileData = processProjectData(rawUserData);
          setCurrentUser(prevUser => ({
            ...(prevUser as User), // Cast because prevUser could be null initially
            ...userProfileData,
            // Ensure id, email, createdAt are preserved if not in updates
            id: prevUser?.id || userId,
            email: prevUser?.email || '', // Email shouldn't be updated here
            createdAt: prevUser?.createdAt || '',
          }));
        }
      }
      // Real-time listener will automatically refresh the list of all users
      return true;
    } catch (error) {
      return false;
    }
  };

  const deleteUser = async (userId: string): Promise<boolean> => {
    if (currentUser && userId === currentUser.id) {
      alert("Cannot delete own account.");
      return false;
    }
    try {
      // Cloud function atomically deletes Auth user + Firestore profile.
      // Deleting Firestore first on the client would orphan the Auth account if the
      // cloud call later failed.
      const functionsInstance = getFunctions();
      const callDeleteAuthUser = httpsCallable(functionsInstance, 'deleteauthuser');
      await callDeleteAuthUser({ uid: userId });

      // Real-time listener will automatically refresh user list
      return true;
    } catch (error: any) {
      return false;
    }
  };

  const getAllUsers = (): User[] => allUsers;
  const getUsersByTeam = (team: TeamName): User[] => allUsers.filter(user => user.team === team);
  const getSupervisorsByTeams = (teams: TeamName[]): User[] => allUsers.filter(user => user.role === 'supervisor' && teams.includes(user.team));
  const isUserAuthorizedForProject = (userToCheck: User | null, project: Project | null): boolean => {
    if (!userToCheck || !project) return false;
    return userToCheck.role === 'admin' || project.teamsInvolved.includes(userToCheck.team);
  };

  const authContextValue: AuthContextType = {
    currentUser, users: allUsers, login, logout, updateUser, deleteUser,
    changePassword, adminSetUserPassword, authLoading, getAllUsers, getUsersByTeam, getSupervisorsByTeams,
    isUserAuthorizedForProject, registerUser,
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      {authLoading ? (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="text-center">
            <div className="animate-spin rounded-full w-16 h-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">Initializing Authentication...</p>
          </div>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
